import {AbstractParameter, IntegerParameter} from "parameters/AbstractParameter.js";
import JakiConnectable from './JakiConnectable.js';
import Registered from "utils/Registered.js";
import {uuid} from 'uuidv4';
import NodeMessaging from "messaging/NodeMessaging.js";
import {BOTH, LEFT, RIGHT, NONE} from './constants.js';

export default class JakiParams extends JakiConnectable(NodeMessaging(Registered(class {}))) {

    constructor(subSequence, zObject) {
        super();
        this.subSequence = subSequence;
        this.zObject = zObject;
        this.sequencerEngine = subSequence.sequencerEngine;
        this.id=uuid();
        this.setupShiftParameters();
    }


    getConnectedTidal(number=0) {
        let outlets = this.zObject && this.zObject.outlets;
        return super.getConnectedTidal(number, "params", outlets);
    }

    setupShiftParameters() {
        this.seedParameter = new IntegerParameter(
            'seed',
            this,
            (x) => [],
            {
                defaultValue: 1,
                min: 1,
                max: 64000
            });
        this.registerParameter(this.seedParameted);

        this.shift1Parameter = this.newShiftParameter(1);
        this.shift2Parameter = this.newShiftParameter(2);
        this.shift3Parameter = this.newShiftParameter(3);
        this.shift4Parameter = this.newShiftParameter(4);
        this.shift5Parameter = this.newShiftParameter(5);
        this.shift6Parameter = this.newShiftParameter(6);

        this.hand1Parameter = this.newHandParameter(1);
        this.hand2Parameter = this.newHandParameter(2);
        this.hand3Parameter = this.newHandParameter(3);
        this.hand4Parameter = this.newHandParameter(4);
        this.hand5Parameter = this.newHandParameter(5);
        this.hand6Parameter = this.newHandParameter(6);

        this.scale1Parameter = this.newScaleParameter(1);
        this.scale2Parameter = this.newScaleParameter(2);
        this.scale3Parameter = this.newScaleParameter(3);
        this.scale4Parameter = this.newScaleParameter(4);
        this.scale5Parameter = this.newScaleParameter(5);
        this.scale6Parameter = this.newScaleParameter(6);

        this.truncate1Parameter = this.newTruncateParameter(1);
        this.truncate2Parameter = this.newTruncateParameter(2);
        this.truncate3Parameter = this.newTruncateParameter(3);
        this.truncate4Parameter = this.newTruncateParameter(4);
        this.truncate5Parameter = this.newTruncateParameter(5);
        this.truncate6Parameter = this.newTruncateParameter(6);

        this.tempo1Parameter = this.newTempoParameter(1);
        this.tempo2Parameter = this.newTempoParameter(2);
        this.tempo3Parameter = this.newTempoParameter(3);
        this.tempo4Parameter = this.newTempoParameter(4);
        this.tempo5Parameter = this.newTempoParameter(5);
        this.tempo6Parameter = this.newTempoParameter(6);

        this.rule1Parameter = this.newRuleParameter(1);
        this.rule2Parameter = this.newRuleParameter(2);
        this.rule3Parameter = this.newRuleParameter(3);
        this.rule4Parameter = this.newRuleParameter(4);
        this.rule5Parameter = this.newRuleParameter(5);
        this.rule6Parameter = this.newRuleParameter(6);

        this.swing1Parameter = this.newSwingParameter(1);
        this.swing2Parameter = this.newSwingParameter(2);
        this.swing3Parameter = this.newSwingParameter(3);
        this.swing4Parameter = this.newSwingParameter(4);
        this.swing5Parameter = this.newSwingParameter(5);
        this.swing6Parameter = this.newSwingParameter(6);

        this.offset1Parameter = this.newOffsetParameter(1);
        this.offset2Parameter = this.newOffsetParameter(2);
        this.offset3Parameter = this.newOffsetParameter(3);
        this.offset4Parameter = this.newOffsetParameter(4);
        this.offset5Parameter = this.newOffsetParameter(5);
        this.offset6Parameter = this.newOffsetParameter(6);

        this.triplet1Parameter = this.newTripletParameter(1);
        this.triplet2Parameter = this.newTripletParameter(2);
        this.triplet3Parameter = this.newTripletParameter(3);
        this.triplet4Parameter = this.newTripletParameter(4);
        this.triplet5Parameter = this.newTripletParameter(5);
        this.triplet6Parameter = this.newTripletParameter(6);

        this.newInlet("shift", (m) => this.onMsg("shift", m));
        this.newInlet("truncate", (m) => this.onMsg("truncate", m));
        this.newInlet("tempo", (m) => this.onMsg("tempo", m));
        this.newInlet("hand", (m) => this.onMsg("hand", m));
        this.newInlet("swing", (m) => this.onMsg("swing", m));
        this.newInlet("offset", (m) => this.onMsg("offset", m));
    } 

    onMsg(type, msg) {
        let numbers = [1,2,3,4,5,6];
        let params = numbers.map(
            x => this[type + x + "Parameter"]);

        msg = msg[0];
        if (!Array.isArray(msg)) {
            // then we set all the parameters to the value
            params.forEach(
                s => s.set(msg));
        } else {
            params.forEach(
                (s,i) => s.set(msg[i%msg.length]));
        }
    }

    newRuleParameter(n) {
        let param = new IntegerParameter(
            "rule" + n, this, () => 0,
            {
                defaultValue: null,
                min: 0,
                max: 12
            });
        this.registerParameter(param);
        return param;
    }

    newShiftParameter(n) {
        let param = new IntegerParameter(
            "shift" + n, this, () => 0,
            {
                defaultValue: 0,
                min: 0,
                max: 16
            });
        this.registerParameter(param);
        return param;
    }

    newScaleParameter(n) {
        let param = new AbstractParameter(
            "scale" + n, this, () => 0,
            {
                defaultValue: false,
            });
        this.registerParameter(param);
        return param;
    }

    newHandParameter(n) {
        let param = new IntegerParameter(
            "hand" + n, this, () => 0,
            {
                defaultValue: BOTH,
                min: 0,
                max: 3
            });
        this.registerParameter(param);
        return param;
    }

    newTruncateParameter(n) {
        let param = new IntegerParameter(
            "truncate" + n, this, () => 0,
            {
                defaultValue: 0,
                min: 0,
                max: 5
            });
        this.registerParameter(param);
        return param;
    }

    newTempoParameter(n) {
        let param = new IntegerParameter(
            "tempo" + n, this, () => 0,
            {
                defaultValue: 2,
                min: 1,
                max: 5
            });
        this.registerParameter(param);
        return param;
    }

    newSwingParameter(n) {
        let param = new IntegerParameter(
            "swing" + n, this, () => 0,
            {
                defaultValue: 50,
                min: 50,
                max: 100
            });
        this.registerParameter(param);
        return param;
    }

    newOffsetParameter(n) {
        let param = new IntegerParameter(
            "offset" + n, this, () => 0,
            {
                defaultValue: 0,
                min: 0,
                max: 100
            });
        this.registerParameter(param);
        return param;
    }

    newTripletParameter(n) {
        let param = new AbstractParameter(
            "triplet" + n, this, () => 0,
            {
                defaultValue: false,
            });
        this.registerParameter(param);
        return param;
    }
}
